import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TrophyIcon, SpinnerGapIcon, RocketLaunchIcon, MedalIcon } from "@phosphor-icons/react";
import { fetchDevLeaderboard } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { fmtUsd } from "../lib/format";

const GOLD = "#F0C000";

const rateColor = (r) => (r >= 60 ? "#00E599" : r >= 35 ? GOLD : r >= 15 ? "#F0A020" : "#888");
const multColor = (m) => (m >= 5 ? "#22D3EE" : m >= 2 ? "#00E599" : m >= 1.2 ? GOLD : "#888");
const rankTint = (i) => (i === 0 ? "#F0C000" : i === 1 ? "#C0C7D0" : i === 2 ? "#CD7F32" : "#555");

const DevRow = ({ d, rank }) => (
  <div data-testid={`dev-lb-row-${rank}`} className="flex flex-col sm:flex-row sm:items-center gap-3 border border-white/10 bg-card p-4 hover:border-white/25 transition-colors">
    <div className="flex items-center gap-3 min-w-0 sm:w-48 shrink-0">
      <span className="font-heading font-black text-lg w-6 text-center shrink-0" style={{ color: rankTint(rank - 1) }}>{rank}</span>
      <div className="min-w-0">
        <div className="font-heading font-bold truncate flex items-center gap-1.5">
          {rank <= 3 && d.hasData && <MedalIcon size={15} weight="fill" style={{ color: rankTint(rank - 1) }} />}
          {d.name}
        </div>
        <div className="font-mono text-[10px] text-muted-foreground">{d.walletCount} wallet{d.walletCount === 1 ? "" : "s"} · {d.coins} coin{d.coins === 1 ? "" : "s"} tracked</div>
      </div>
    </div>

    {d.hasData ? (
      <>
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="text-center">
            <div className="font-heading font-black text-2xl leading-none" style={{ color: rateColor(d.hitRate) }} data-testid={`dev-lb-hitrate-${rank}`}>{d.hitRate}%</div>
            <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">hit rate ≥2×</div>
          </div>
          <div className="text-center">
            <div className="font-heading font-black text-xl leading-none" style={{ color: multColor(d.bestMultiple) }}>{d.bestMultiple}×</div>
            <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">best</div>
          </div>
          <div className="text-center">
            <div className="font-heading font-black text-xl leading-none text-white/90">{d.avgMultiple}×</div>
            <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">avg</div>
          </div>
          {d.moonshots > 0 && (
            <div className="text-center">
              <div className="font-heading font-black text-xl leading-none" style={{ color: "#22D3EE" }}>{d.moonshots}</div>
              <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">5×+ 🚀</div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
          {d.topCoins.map((c) => (
            <Link key={c.mint} to={`/coin/${c.mint}`} className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-1 border border-white/10 hover:border-neon/40 transition-colors" title={`${fmtUsd(c.firstMcap)} → ${fmtUsd(c.peakMcap)}`}>
              <span className="font-bold">${c.symbol || c.mint.slice(0, 4)}</span>
              <span style={{ color: multColor(c.peakMultiple) }}>{c.peakMultiple}×</span>
            </Link>
          ))}
        </div>
      </>
    ) : (
      <div className="text-xs font-mono text-muted-foreground sm:ml-auto">No launches tracked yet — stats appear once we see their coins.</div>
    )}
  </div>
);

export const DevLeaderboard = () => {
  const clientId = getClientId();
  const { data, isLoading } = useQuery({
    queryKey: ["devLeaderboard", clientId],
    queryFn: () => fetchDevLeaderboard(clientId),
    refetchInterval: 120000,
  });
  const devs = data?.devs || [];

  return (
    <div className="space-y-4" data-testid="dev-leaderboard">
      <div className="flex items-center gap-2.5">
        <TrophyIcon size={22} weight="fill" style={{ color: GOLD }} />
        <h2 className="font-heading font-black text-xl sm:text-2xl tracking-tighter leading-none">ALPHA DEV LEADERBOARD</h2>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl -mt-1">
        Your tracked devs ranked by <span className="font-semibold" style={{ color: GOLD }}>past hit-rate</span> — the share of
        their coins that ran <span className="text-neon font-semibold">≥2×</span> from when we first spotted them. The best
        builders rise to the top. Based on our free peak-market-cap history; new devs need a few tracked launches first.
      </p>
      {isLoading ? (
        <div className="flex justify-center py-14"><SpinnerGapIcon size={28} className="animate-spin" style={{ color: GOLD }} /></div>
      ) : devs.length === 0 ? (
        <div className="border border-white/10 bg-card py-14 text-center text-muted-foreground font-mono text-sm px-6" data-testid="dev-leaderboard-empty">
          <RocketLaunchIcon size={26} className="mx-auto mb-3 opacity-60" />
          Add some Alpha devs to see them ranked by hit-rate here.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {devs.map((d, i) => <DevRow key={d.name + i} d={d} rank={i + 1} />)}
        </div>
      )}
    </div>
  );
};
